import React, { useEffect, useState } from "react";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { api } from "../../api/client";

// Bordo di 4px sfumato nei colori dell'onboarding (blu, viola, verde), un po'
// schiariti: il bianco della card fa da fondo, il gradiente resta solo sul bordo.
const BORDO_ONBOARDING = {
  border: "4px solid transparent",
  background:
    "linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, rgba(66,133,244,0.55), rgba(156,78,255,0.55) 50%, rgba(52,168,83,0.55)) border-box",
};

const MIN = 10;
const MAX = 2000;

// Esempi del placeholder animato, come nella casella del prompt
// dell'onboarding (PromptIntro).
const ESEMPI = [
  "Un modulo per chi vuole un sopralluogo per l'impianto fotovoltaico: nome, telefono, comune e tipo di immobile (casa, azienda, capannone).",
  "Per chi chiede un preventivo per il matrimonio: nomi, data, numero di invitati e budget indicativo.",
  "Per chi vuole prenotare una visita: nome, telefono, giorno preferito e motivo della visita.",
];
const TYPE_MS = 26;
const DELETE_MS = 11;
const HOLD_MS = 2400;
const GAP_MS = 450;

// Placeholder a macchina da scrivere: scrive un esempio, pausa, cancella e
// passa al successivo. Con "riduci movimento" resta il primo, fermo.
function usePlaceholderAnimato(esempi) {
  const [testo, setTesto] = useState("");
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      setTesto(esempi[0]);
      return undefined;
    }
    let index = 0;
    let pos = 0;
    let cancella = false;
    let timer;
    const tick = () => {
      const intero = esempi[index];
      if (!cancella) {
        pos += 1;
        const fine = pos >= intero.length;
        setTesto(intero.slice(0, pos) + (fine ? "" : "▍"));
        if (fine) {
          cancella = true;
          timer = setTimeout(tick, HOLD_MS);
          return;
        }
        timer = setTimeout(tick, TYPE_MS);
      } else {
        pos = Math.max(0, pos - 3);
        setTesto(intero.slice(0, pos) + (pos > 0 ? "▍" : ""));
        if (pos === 0) {
          cancella = false;
          index = (index + 1) % esempi.length;
          timer = setTimeout(tick, GAP_MS);
          return;
        }
        timer = setTimeout(tick, DELETE_MS);
      }
    };
    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, [esempi]);
  return testo;
}

/**
 * "Descrivi il modulo": il bot prepara la bozza dei campi, come nella
 * creazione del bot, e con lo stesso aspetto (card bianca con il bordo nei
 * colori dell'onboarding, pulsante viola). La bozza finisce nel compositore
 * qui sotto e non si salva finche' il cliente non preme Salva.
 *
 * @param {string} botId
 * @param {(form: Object, warnings: string[]) => void} onDraft
 * @param {(message: string) => void} onError
 */
export default function ContactFormDraft({ botId, onDraft, onError }) {
  const [descrizione, setDescrizione] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const placeholder = usePlaceholderAnimato(ESEMPI);
  const testo = descrizione.trim();
  const pronto = !inCorso && testo.length >= MIN;

  const prepara = async () => {
    if (!pronto) return;
    setInCorso(true);
    try {
      const risultato = await api.contacts.draftForm(botId, testo);
      onDraft(risultato.form, risultato.warnings || []);
    } catch (error) {
      const errors = error?.data?.errors || error?.response?.data?.errors;
      onError(errors?.length ? errors.join(" ") : error?.message || "Riprova fra poco.");
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div
      className="rounded-[14px] p-4 shadow-[0_10px_7.5px_rgba(0,0,0,0.05),0_4px_3px_rgba(0,0,0,0.05)]"
      style={BORDO_ONBOARDING}
    >
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[#020617]">
        <Sparkles className="h-4 w-4 text-[#AD46FF]" aria-hidden="true" />
        Descrivi il modulo
      </p>
      <div className="relative">
        <textarea
          rows={3}
          maxLength={MAX}
          value={descrizione}
          placeholder={placeholder}
          onChange={(e) => setDescrizione(e.target.value)}
          onKeyDown={(e) => {
            // Invio prepara la bozza, Shift+Invio va a capo (come nell'onboarding).
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              prepara();
            }
          }}
          aria-label="Descrizione del modulo"
          className="min-h-[72px] w-full resize-none border-0 bg-transparent text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/80"
        />
        <span className="pointer-events-none absolute -bottom-1 right-0 text-[11px] text-muted-foreground/70">
          {descrizione.length}/{MAX}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-sm text-xs leading-4 text-[#64748B]">
          Preparo i campi al posto di quelli qui sotto; non salvo nulla finché non premi Salva.
        </p>
        <button
          type="button"
          onClick={prepara}
          disabled={!pronto}
          className="inline-flex h-10 items-center rounded-lg bg-[#AD46FF] px-5 text-sm font-medium text-white transition-colors hover:bg-[#9d3ee6] disabled:pointer-events-none disabled:opacity-50"
        >
          {inCorso ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {inCorso ? "Preparo…" : "Prepara la bozza"}
          {!inCorso ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /> : null}
        </button>
      </div>
    </div>
  );
}
